import React, { useState } from 'react';
import { TextField, Button, Paper, Typography, Grid, Box, Snackbar, Alert } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
// eslint-disable-next-line
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import './ContactUs.css';
import { postApiCall } from '../utils/Apicalls'; // adjust path if needed
import Header from "../components/common/header/Header";
import Footer from "../components/common/footer/Footer";

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // const response = await fetch('interview-app/contactus', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(formData),
      // });
      const data = await postApiCall('interview-app/contactus', formData);
      console.log('Data:', data);
      // const data = await response.json();
      if (data && data.status) {
        setSnackbar({
          open: true,
          message: 'Thank you for your message! We will get back to you soon.',
          severity: 'success'
        });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSnackbar({
          open: true,
          message: data && data.message ? data.message : 'Failed to submit form. Please try again later.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit form. Please try again later.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  return (
    <>       
      <Header />
        <div className="contact-us-container" id="contact">
          <Typography variant="h2" className="section-title text-white" gutterBottom>
            Contact Us
          </Typography>
          
          <Grid container spacing={4}>
            {/* Contact Information */}
            <Grid item xs={12} md={5}>
              <Paper elevation={3} className="contact-info-paper">
                <Typography variant="h5" gutterBottom className="contact-info-title">
                  <ContactSupportIcon fontSize="large" className="contact-icon-header" />
                  Get in Touch
                </Typography>
                
                <Typography variant="body1" paragraph>
                  Have questions about our services? Need help with your interview preparation?
                  Our team is here to help you succeed in your career journey.
                </Typography>
                
                <Box className="contact-method">
                  <WhatsAppIcon className="contact-icon" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      WhatsApp
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="success" 
                      href="https://wa.me/16576307025?text=InterviewSupportAI" 
                      target="_blank"
                      startIcon={<WhatsAppIcon />}
                      className="whatsapp-button"
                    >
                      Chat with Us
                    </Button>
                  </Box>
                </Box>
                
                <Box className="contact-method">
                  <EmailIcon className="contact-icon" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Email
                    </Typography>
                    <Typography variant="body2">
                      <a href="mailto:info@interviewsupport-ai.com" className="contact-link">
                        info@interviewsupport-ai.com
                      </a>
                    </Typography>
                  </Box>
                </Box>
                
                <Box className="contact-method">
                  <PhoneIcon className="contact-icon" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      <a href="tel:+19092359247" className="contact-link">
                        +1 909-235-9247
                      </a>
                    </Typography>
                  </Box>
                </Box>
                
                {/* <Box className="contact-method">
                  <LocationOnIcon className="contact-icon" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Address
                    </Typography>
                    <Typography variant="body2">
                      201 E Center St Suite 112, Anaheim CA 92805
                    </Typography>
                  </Box>
                </Box> */}
              </Paper>
            </Grid>
            
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <Paper elevation={3} className="contact-form-paper">
                <Typography variant="h5" gutterBottom className="form-title">
                  Send Us a Message
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        error={!!errors.message}
                        helperText={errors.message}
                        required
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        className="submit-button"
                      >
                        Send
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>
          
          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={6000} 
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </div>      
      <Footer />
    </>
  );
}

export default ContactUs;
